import { Component, type ReactNode } from "react"
import { Button } from "../ui/button"
import { Warning, ArrowClockwise, ArrowsClockwise } from "@phosphor-icons/react"
import { useErrorOverlayStore } from "../../stores/error-overlay-store"
import { isChunkLoadError } from "../../lib/lazy-retry"

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("AppErrorBoundary caught a fatal error:", error, errorInfo)

    useErrorOverlayStore.getState().addError({
      message: error.message,
      source: "react",
      stack: error.stack,
      componentStack: errorInfo.componentStack ?? undefined,
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      const isChunk = this.state.error && isChunkLoadError(this.state.error)

      if (isChunk) {
        return (
          <div className="flex items-center justify-center h-screen bg-background">
            <div className="text-center max-w-md p-8">
              <ArrowsClockwise className="h-12 w-12 mx-auto text-primary mb-4" weight="duotone" />
              <h1 className="text-xl font-semibold mb-2">App Updated</h1>
              <p className="text-sm text-muted-foreground mb-6">
                A new version has been deployed. Please reload to continue.
              </p>
              <Button onClick={this.handleReload}>Reload Page</Button>
            </div>
          </div>
        )
      }

      return (
        <div className="flex items-center justify-center h-screen bg-background">
          <div className="text-center max-w-md p-8">
            <Warning className="h-12 w-12 mx-auto text-warning mb-4" weight="duotone" />
            <h1 className="text-xl font-semibold mb-2">Looks like we hit an issue</h1>
            <p className="text-sm text-muted-foreground mb-6">
              No worries — you can try to recover where you left off or reload the page.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={this.handleReset}>
                <ArrowClockwise className="h-4 w-4 mr-2" />
                Try to Recover
              </Button>
              <Button onClick={this.handleReload}>Reload Page</Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
